import type { ModalComponent, ModalEntry, ModalOpenOptions } from './types'

type Listener = () => void

let current: ModalEntry | null = null
const listeners = new Set<Listener>()

function notify(): void {
  const list = Array.from(listeners)
  list.forEach((l) => setTimeout(() => l(), 0))
}

/**
 * Imperative modal manager. No React Context – call from any component or page.
 * - open(Component, props, options?) to show a modal; options can restrict by route or shouldRender()
 * - close() to hide
 * - ModalHost must be mounted once (e.g. in App) to render the active modal
 */
export const modalManager = {
  open<T extends Record<string, unknown>>(
    Component: ModalComponent<T>,
    props?: Omit<T, keyof import('./types').ModalInjectedProps>,
    options?: ModalOpenOptions
  ): void {
    current = {
      Component: Component as ModalEntry['Component'],
      props: (props ?? {}) as Record<string, unknown>,
      options,
    }
    notify()
  },

  close(): void {
    current = null
    notify()
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  getCurrent(): ModalEntry | null {
    return current
  },
}
